// TeamsConnectModal — per-agent Microsoft Teams connection (2026-08-31).
// The agent creates a Teams Workflow ("When a Teams webhook request is
// received" → post to their chat/channel) and pastes the webhook URL here.
// Ticket assignments, client replies, dev-queue messages and project updates
// then arrive in their Teams as cards that deep-link back to the record.
import { useState, useEffect } from "react";

const API = "https://ticketing.midtc.com/api";
const tok = () => localStorage.getItem("midtc_token") || "";
const apiFetch = (path, opts = {}) =>
  fetch(API + path, {
    headers: { Authorization: `Bearer ${tok()}`, "Content-Type": "application/json" },
    ...opts,
  });

const TOGGLES = [
  { k: "notify_assignments", l: "Ticket assignments",  d: "When a ticket is assigned to you (primary or secondary)" },
  { k: "notify_responses",   l: "Client replies",      d: "When a client replies on a ticket assigned to you" },
  { k: "notify_dev",         l: "Dev Queue",           d: "Messages on dev tickets you're on" },
  { k: "notify_projects",    l: "Projects",            d: "Project messages, tasks assigned to you, status changes" },
];

export default function TeamsConnectModal({ onClose }) {
  const [conn, setConn]       = useState(null);
  const [webhook, setWebhook] = useState("");
  const [saving, setSaving]   = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg]         = useState(null); // {text, ok}
  const [showHow, setShowHow] = useState(false);

  useEffect(() => {
    apiFetch("/me/teams-connection").then(r => r.json()).then(d => setConn(d)).catch(() => {});
  }, []);

  const save = async (extra = {}) => {
    setSaving(true); setMsg(null);
    try {
      const body = { ...extra };
      if (webhook.trim()) body.webhook_url = webhook.trim();
      const r = await apiFetch("/me/teams-connection", { method: "PUT", body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) { setMsg({ text: d.error || "Save failed", ok: false }); return; }
      setConn(d); setWebhook("");
      setMsg({ text: "Saved ✓", ok: true });
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setMsg(null);
    try {
      const r = await apiFetch("/me/teams-connection/test", { method: "POST" });
      const d = await r.json();
      setMsg(r.ok ? { text: "Test card sent — check your Teams", ok: true } : { text: d.error || "Test failed", ok: false });
    } finally { setTesting(false); }
  };

  const ipt = { background: "var(--t-input, #0f172a)", border: "1px solid var(--t-border-soft, #1e293b)", borderRadius: 7, padding: "8px 10px", color: "var(--t-text-primary, #f1f5f9)", fontSize: 12.5, width: "100%", boxSizing: "border-box" };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--t-panel-hi, #101a2e)", border: "1px solid var(--t-border-soft, #1e293b)", borderRadius: 14, width: "100%", maxWidth: 480, maxHeight: "88vh", overflow: "auto", boxShadow: "0 25px 60px rgba(0,0,0,0.5)" }}>
        <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid var(--t-border-soft, #1e293b)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--t-text-primary, #f1f5f9)" }}>💬 Teams Updates</h2>
            <div style={{ fontSize: 11, color: "var(--t-text-faint, #64748b)", marginTop: 3 }}>
              {conn?.connected
                ? <span style={{ color: "#34d399" }}>● Connected {conn.webhook_url ? `(${conn.webhook_url})` : ""}</span>
                : <span style={{ color: "#f59e0b" }}>○ Not connected yet</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#64748b", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>

        <div style={{ padding: "16px 22px", display: "flex", flexDirection: "column", gap: 14 }}>
          {msg && (
            <div style={{ background: msg.ok ? "rgba(52,211,153,0.1)" : "rgba(239,68,68,0.1)", border: `1px solid ${msg.ok ? "rgba(52,211,153,0.3)" : "rgba(239,68,68,0.3)"}`, color: msg.ok ? "#34d399" : "#f87171", borderRadius: 7, padding: "8px 12px", fontSize: 12.5 }}>
              {msg.text}
            </div>
          )}

          <div>
            <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 5, fontWeight: 600 }}>
              Teams workflow webhook URL {conn?.connected && <span style={{ fontWeight: 400, color: "#64748b" }}>(paste a new one to replace)</span>}
            </label>
            <input type="text" value={webhook} onChange={e => setWebhook(e.target.value)}
              placeholder="https://…logic.azure.com/… or powerautomate URL" style={ipt} />
            <button onClick={() => setShowHow(s => !s)}
              style={{ background: "none", border: "none", color: "#60a5fa", fontSize: 11.5, cursor: "pointer", padding: "6px 0 0", fontFamily: "inherit" }}>
              {showHow ? "Hide instructions" : "How do I get a webhook URL?"}
            </button>
            {showHow && (
              <ol style={{ fontSize: 11.5, color: "#94a3b8", lineHeight: 1.7, margin: "6px 0 0", paddingLeft: 18 }}>
                <li>In <b>Teams</b>, open <b>Workflows</b> (⋯ → Workflows, or the Workflows app)</li>
                <li>Choose the template <b>"Post to a channel when a webhook request is received"</b> (or "…to a chat")</li>
                <li>Pick the team/channel or chat where you want your updates</li>
                <li>Finish the wizard — Teams shows you the <b>webhook URL</b></li>
                <li>Copy it, paste it above, Save, then hit <b>Send test</b></li>
              </ol>
            )}
          </div>

          {conn && (
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>What lands in your Teams</div>
              {TOGGLES.map(t => (
                <label key={t.k} style={{ display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer", background: "var(--t-panel, #0d1526)", border: "1px solid var(--t-border-soft, #1e293b)", borderRadius: 8, padding: "8px 11px" }}>
                  <input type="checkbox" checked={conn[t.k] !== false}
                    onChange={e => { setConn(c => ({ ...c, [t.k]: e.target.checked })); save({ [t.k]: e.target.checked }); }}
                    style={{ marginTop: 2, accentColor: "#4f6fff" }} />
                  <span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--t-text-body, #cbd5e1)", display: "block" }}>{t.l}</span>
                    <span style={{ fontSize: 11, color: "#64748b" }}>{t.d}</span>
                  </span>
                </label>
              ))}
              <label style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer", padding: "4px 2px" }}>
                <input type="checkbox" checked={conn.enabled !== false}
                  onChange={e => { setConn(c => ({ ...c, enabled: e.target.checked })); save({ enabled: e.target.checked }); }}
                  style={{ accentColor: "#4f6fff" }} />
                <span style={{ fontSize: 12, color: "var(--t-text-body, #cbd5e1)" }}>Teams notifications enabled</span>
              </label>
            </div>
          )}
        </div>

        <div style={{ padding: "12px 22px 16px", borderTop: "1px solid var(--t-border-soft, #1e293b)", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={test} disabled={testing || !conn?.connected}
            style={{ background: "none", border: "1px solid var(--t-border-soft, #1e293b)", borderRadius: 7, padding: "8px 16px", color: conn?.connected ? "#94a3b8" : "#475569", fontSize: 12.5, cursor: conn?.connected ? "pointer" : "default", fontFamily: "inherit" }}>
            {testing ? "Sending…" : "Send test"}
          </button>
          <button onClick={() => save()} disabled={saving || !webhook.trim()}
            style={{ background: webhook.trim() ? "#4f6fff" : "var(--t-surface-mid, #1e293b)", color: webhook.trim() ? "#fff" : "#64748b", border: "none", borderRadius: 7, padding: "8px 18px", fontSize: 12.5, fontWeight: 600, cursor: webhook.trim() ? "pointer" : "default", fontFamily: "inherit" }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
